import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import Link from "@material-ui/core/Link";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Select from "@material-ui/core/Select";
import Slider from "@material-ui/core/Slider";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import { Formik } from "formik";
import moment from "moment";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import FileSelector from "@components/FileSelector";
import GroupInput from "@components/GroupInput";
import MessagesSelector from "@components/MessagesSelector";
import PageBody from "@components/PageBody";
import PageControl from "@components/PageControl";
import MessageService from "@services/Message";
import AdminPageTemplate from "@templates/AdminPage";
import { roundMinutes } from "@utils/datetime";
import {
  DestinationInputTypeEnum,
  DestinationTypeEnum,
  FileFormatEnum,
} from "@utils/message";
import { formatNativeDatetime } from "@utils/datetime";
import { generateUniqueFileName } from "@utils/file";
import { uniqueKey } from "@utils/react";

const useStyles = makeStyles((theme) => ({
  formInputs: {
    marginTop: "50px",
  },
  divider: {
    marginTop: "12px",
    marginBottom: "12px",
  },
  sliderInput: {
    marginTop: "18px",
  },
  destinationInputTypeSelector: {
    marginTop: "8px",
  },
  destinationFileExample: {
    display: "table",
    marginTop: "10px",
  },
}));

const DEFAULT_EXPIRY_TIME = 1439;

const DestinationFileTypeEnum = {
  NUMBERS: "numbers",
  NUMBERS_AND_MESSAGES: "numbers_and_messages",
};

const ShippingTypeEnum = {
  IMMEDIATELY: "IMMEDIATELY",
  SCHEDULED: "SCHEDULED",
};

const destinationFileTypeOptions = [
  {
    label: "Números",
    value: DestinationFileTypeEnum.NUMBERS,
  },
  {
    label: "Números e Mensagens",
    value: DestinationFileTypeEnum.NUMBERS_AND_MESSAGES,
  },
];

const destinationFileTypeExamples = {
  [DestinationFileTypeEnum.NUMBERS]: {
    href: "/files/examples/numbers.txt",
    label: "Exemplo de arquivo de números de destinos",
  },
  [DestinationFileTypeEnum.NUMBERS_AND_MESSAGES]: {
    href: "/files/examples/numbers_and_messages.txt",
    label: "Exemplo de arquivo de números e mensagens de destinos",
  },
};

function hasInvalidChars(value) {
  return /[^0-9;]/gi.test(value);
}

function Message(props) {
  const classes = useStyles();

  const accountId = useSelector((state) => state.account.id);
  const accountShortcodes = useSelector((state) => state.account.shortcodes);

  const [destinationInputType, setDestinationInputType] = useState(
    DestinationInputTypeEnum.SINGLE
  );
  const [sendingMessage, setSendingMessage] = useState(false);

  function mountMessageData(values, uniqueFileName) {
    const requestData = {
      accountId: accountId,
      template: {
        senderId: values.sourceShortcode.value,
        validity: values.expiryTime,
      },
    };

    if (values.shippingType === ShippingTypeEnum.SCHEDULED) {
      requestData.deliveryDatetime = formatNativeDatetime(
        values.scheduleDatetime
      );
    }

    if (destinationInputType === DestinationInputTypeEnum.SINGLE) {
      requestData.destinationType = DestinationTypeEnum.LIST;
      requestData.destination = {
        addressList: values.destinationNumbers.split(";"),
      };
    } else if (destinationInputType === DestinationInputTypeEnum.GROUP) {
      requestData.destinationType = DestinationTypeEnum.SAVED_LIST;
      requestData.destination = {
        listId: values.destinationGroup.id,
      };
    } else if (destinationInputType === DestinationInputTypeEnum.FILE) {
      requestData.destinationType = DestinationTypeEnum.FILE;
      if (
        values.destinationFileType ===
        DestinationFileTypeEnum.NUMBERS_AND_MESSAGES
      ) {
        requestData.destination = {
          file: {
            name: uniqueFileName,
            format: FileFormatEnum.DESTINATION_WITH_MESSAGE,
          },
        };
      } else if (
        values.destinationFileType === DestinationFileTypeEnum.NUMBERS
      ) {
        requestData.destination = {
          file: {
            name: uniqueFileName,
            format: FileFormatEnum.DESTINATION_ONLY,
          },
        };
      }
    }

    return requestData;
  }

  function mountMessageDataForTypedMessage(
    values,
    textMessage,
    uniqueFileName
  ) {
    const requestData = mountMessageData(values, uniqueFileName);
    requestData.template.messageType = textMessage.contentType;
    requestData.template.textMessage = textMessage.message;
    return requestData;
  }

  function mountMessageDataForFileWithMessage(values, uniqueFileName) {
    const requestData = mountMessageData(values, uniqueFileName);
    requestData.template.messageType = FileFormatEnum.DESTINATION_WITH_MESSAGE;
    requestData.template.textMessage = "";
    return requestData;
  }

  function createUniqueFileName(file) {
    const uniqueFileName = generateUniqueFileName(file);
    const uploadFileName = `${accountId}/${uniqueFileName}`;
    return uploadFileName;
  }

  function sendMessage(values, done) {
    if (sendingMessage) {
      return;
    }

    setSendingMessage(true);

    const requests = [];

    let uniqueFileName = "";
    if (destinationInputType === DestinationInputTypeEnum.FILE) {
      uniqueFileName = createUniqueFileName(values.destinationFile);
    }

    if (
      destinationInputType === DestinationInputTypeEnum.FILE &&
      values.destinationFileType ===
        DestinationFileTypeEnum.NUMBERS_AND_MESSAGES
    ) {
      requests.push(mountMessageDataForFileWithMessage(values, uniqueFileName));
    } else {
      values.messages.forEach((message) => {
        requests.push(
          mountMessageDataForTypedMessage(values, message, uniqueFileName)
        );
      });
    }

    let requestPromise;
    if (destinationInputType === DestinationInputTypeEnum.FILE) {
      requestPromise = MessageService.sendWithFile(
        requests,
        values.destinationFile,
        uniqueFileName
      );
    } else {
      requestPromise = MessageService.send(requests);
    }

    requestPromise
      .then(() => {
        done();
        if (values.shippingType === ShippingTypeEnum.IMMEDIATELY) {
          toast.success("Mensagem enviada!");
        } else if (values.shippingType === ShippingTypeEnum.SCHEDULED) {
          toast.success("Mensagem agendada!");
        }
        setSendingMessage(false);
      })
      .catch((e) => {
        console.error(e);
        switch (e.toString()) {
          case "AWS S3 bucket not configured":
            toast.error("Repositório de arquivo não configurado!");
            break;
          case "File upload error":
            toast.error("Erro ao enviar o arquivo!");
            break;
          case "Duplicate file":
            toast.error("Nome de arquivo duplicado!");
            break;
          default:
            if (values.shippingType === ShippingTypeEnum.IMMEDIATELY) {
              toast.error("Erro ao enviar mensagem!");
            } else if (values.shippingType === ShippingTypeEnum.SCHEDULED) {
              toast.error("Erro ao agendar mensagem!");
            }
        }
        setSendingMessage(false);
      });
  }

  function handleChangeDestinationInputType(e, value) {
    setDestinationInputType(value);
  }

  const sourceShortcodeOptions = useMemo(() => {
    return accountShortcodes.map((shortcode) => ({
      label: shortcode.code,
      value: shortcode.code,
    }));
  }, [accountShortcodes]);

  const initialValues = {
    sourceShortcode: null,
    destinationNumbers: "",
    destinationFileType: "",
    destinationFile: null,
    destinationGroup: null,
    shippingType: ShippingTypeEnum.IMMEDIATELY,
    scheduleDatetime: null,
    expiryTime: DEFAULT_EXPIRY_TIME,
    messages: [],
  };

  const validationRules = {
    sourceShortcode: Yup.object().nullable().required("Campo requerido"),
    scheduleDatetime: Yup.mixed().when(["shippingType"], {
      is: (shippingType) => shippingType === ShippingTypeEnum.SCHEDULED,
      then: Yup.mixed()
        .nullable()
        .required("Campo requerido")
        .test("", "Data e hora inválida", (value) => {
          if (typeof value === "undefined") {
            return true;
          }

          const datetime = moment(value);
          if (!datetime.isValid()) {
            return false;
          }

          const now = moment();
          if (datetime < now) {
            return false;
          }

          return true;
        }),
    }),
  };

  if (destinationInputType === DestinationInputTypeEnum.SINGLE) {
    validationRules.destinationNumbers = Yup.string()
      .required("Campo requerido")
      .max(2048, "Limite máximo de 2048 caracteres")
      .test("", "Erro de formatação", (value) => {
        if (value === undefined) {
          return true;
        }
        return /^(\d{10,11};)*\d{10,11}$/gi.test(value);
      });
    validationRules.messages = Yup.array().min(1, "Campo requerido");
  } else if (destinationInputType === DestinationInputTypeEnum.FILE) {
    validationRules.destinationFileType =
      Yup.string().required("Campo requerido");
    validationRules.destinationFile = Yup.mixed()
      .nullable()
      .required("Campo requerido");
    validationRules.messages = Yup.array().when(["destinationFileType"], {
      is: (destinationFileType) =>
        destinationFileType === DestinationFileTypeEnum.NUMBERS,
      then: Yup.array().min(1, "Campo requerido"),
    });
  } else if (destinationInputType === DestinationInputTypeEnum.GROUP) {
    validationRules.destinationGroup = Yup.object()
      .nullable()
      .required("Campo requerido");
    validationRules.messages = Yup.array().min(1, "Campo requerido");
  }
  const validationSchema = Yup.object().shape(validationRules);

  return (
    <AdminPageTemplate breadcrumbs={props.breadcrumbs}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={false}
        validateOnMount={true}
      >
        {(context) => {
          const {
            errors,
            touched,
            isValid,
            values,
            resetForm,
            validateForm,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
          } = context;

          function isSingleMode() {
            return destinationInputType === DestinationInputTypeEnum.SINGLE;
          }

          function isFileMode() {
            return destinationInputType === DestinationInputTypeEnum.FILE;
          }

          function isGroupMode() {
            return destinationInputType === DestinationInputTypeEnum.GROUP;
          }

          function isFileTypeEmpty() {
            return isFileMode() && values.destinationFileType === "";
          }

          function isFileWithMessages() {
            return (
              isFileMode() &&
              values.destinationFileType ===
                DestinationFileTypeEnum.NUMBERS_AND_MESSAGES
            );
          }

          function isSchedule() {
            return values.shippingType === ShippingTypeEnum.SCHEDULED;
          }

          function clearForm() {
            resetForm();
            validateForm();
          }

          return (
            <React.Fragment>
              <PageBody title="Mensagem">
                {/* destination input type */}
                <ToggleButtonGroup
                  value={destinationInputType}
                  exclusive
                  className={classes.destinationInputTypeSelector}
                  onChange={(e, value) => {
                    resetForm();
                    validateForm();
                    handleChangeDestinationInputType(e, value);
                  }}
                >
                  <ToggleButton value={DestinationInputTypeEnum.SINGLE}>
                    Avulsa
                  </ToggleButton>
                  <ToggleButton value={DestinationInputTypeEnum.FILE}>
                    Arquivo
                  </ToggleButton>
                  <ToggleButton value={DestinationInputTypeEnum.GROUP}>
                    Grupo
                  </ToggleButton>
                </ToggleButtonGroup>
                {/* end destination input type */}

                <div className={classes.formInputs}>
                  <Grid container spacing={3}>
                    {/* source shortcode */}
                    <Grid
                      item
                      xs={12}
                      sm={isFileMode() ? 6 : 4}
                      xl={isFileMode() ? 3 : 4}
                    >
                      <Autocomplete
                        value={values.sourceShortcode}
                        name="sourceShortcode"
                        options={sourceShortcodeOptions}
                        getOptionLabel={(option) => {
                          return option ? option.label : "";
                        }}
                        getOptionSelected={(option) =>
                          option.value === values.sourceShortcode.value
                        }
                        fullWidth
                        noOptionsText="Nenhum número encontrado"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Número de Origem *"
                            variant="outlined"
                            helperText={
                              touched.sourceShortcode
                                ? errors.sourceShortcode
                                : ""
                            }
                            error={
                              touched.sourceShortcode &&
                              Boolean(errors.sourceShortcode)
                            }
                          />
                        )}
                        onChange={(event, option) => {
                          setFieldValue("sourceShortcode", option);
                        }}
                        onBlur={() => {
                          setFieldTouched("sourceShortcode", true);
                        }}
                      />
                    </Grid>
                    {/* end source shortcode */}

                    {/* destination numbers */}
                    {isSingleMode() && (
                      <Grid item xs={12} sm={8}>
                        <TextField
                          value={values.destinationNumbers}
                          variant="outlined"
                          fullWidth
                          label="Números de Destino"
                          name="destinationNumbers"
                          multiline
                          rows={2}
                          required
                          helperText={
                            touched.destinationNumbers
                              ? errors.destinationNumbers
                              : ""
                          }
                          error={
                            touched.destinationNumbers &&
                            Boolean(errors.destinationNumbers)
                          }
                          onChange={(e) => {
                            if (!hasInvalidChars(e.target.value)) {
                              handleChange(e);
                            }
                          }}
                          onBlur={handleBlur}
                        />
                        <FormHelperText>
                          * Exemplo: 00123456789;99876543210;99000001111.
                        </FormHelperText>
                      </Grid>
                    )}
                    {/* end destination numbers */}

                    {/* destination file type */}
                    {isFileMode() && (
                      <Grid item xs={12} sm={6} xl={3}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={
                            touched.destinationFileType &&
                            errors.destinationFileType
                          }
                          required
                        >
                          <InputLabel id="file-type-select">
                            Tipo de Arquivo
                          </InputLabel>
                          <Select
                            labelId="file-type-select"
                            name="destinationFileType"
                            label="Tipo de Arquivo *"
                            value={values.destinationFileType}
                            onChange={(e) => {
                              setFieldValue("destinationFile", null);
                              setFieldValue("messages", []);
                              setFieldTouched("messages", false);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          >
                            {destinationFileTypeOptions.map((option) => (
                              <MenuItem key={uniqueKey()} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.destinationFileType &&
                            errors.destinationFileType && (
                              <FormHelperText>
                                {errors.destinationFileType}
                              </FormHelperText>
                            )}
                        </FormControl>
                      </Grid>
                    )}
                    {/* end destination file type */}

                    {/* destination file */}
                    {isFileMode() && (
                      <Grid item xs={12} sm={12} xl={6}>
                        <FileSelector
                          file={values.destinationFile}
                          label="Arquivo"
                          accept={".zip,.txt"}
                          required
                          onChange={(file) => {
                            setFieldValue("destinationFile", file);
                          }}
                        />
                        {values.destinationFileType && (
                          <Link
                            href={
                              destinationFileTypeExamples[
                                values.destinationFileType
                              ].href
                            }
                            target="_blank"
                            className={classes.destinationFileExample}
                          >
                            <Typography>
                              {
                                destinationFileTypeExamples[
                                  values.destinationFileType
                                ].label
                              }
                            </Typography>
                          </Link>
                        )}
                      </Grid>
                    )}
                    {/* end destination file */}

                    {/* destination groups */}
                    {isGroupMode() && (
                      <Grid item xs={12} sm={8}>
                        <GroupInput
                          group={values.destinationGroup}
                          onChange={(value) =>
                            setFieldValue("destinationGroup", value)
                          }
                        />
                      </Grid>
                    )}
                    {/* end destination groups */}

                    <Grid item xs={12}>
                      <Divider className={classes.divider} />
                    </Grid>

                    {/* shipping type */}
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <Typography gutterBottom>Tipo de Envio</Typography>
                        <RadioGroup
                          name="shippingType"
                          value={values.shippingType}
                          row
                          onChange={(e) => {
                            setFieldValue("scheduleDatetime", null);
                            setFieldTouched("scheduleDatetime", false);
                            handleChange(e);
                          }}
                        >
                          <FormControlLabel
                            value={ShippingTypeEnum.IMMEDIATELY}
                            control={<Radio />}
                            label="Imediato"
                          />
                          <FormControlLabel
                            value={ShippingTypeEnum.SCHEDULED}
                            control={<Radio />}
                            label="Agendado"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    {/* end shipping type */}

                    {/* shipping datetime */}
                    {isSchedule() && (
                      <Grid item xs={12} sm={6}>
                        <KeyboardDateTimePicker
                          format="dd/MM/yyyy HH:mm"
                          minDate={new Date()}
                          minutesStep={5}
                          value={values.scheduleDatetime}
                          ampm={false}
                          label="Data/Hora de Envio *"
                          error={
                            touched.scheduleDatetime &&
                            Boolean(errors.scheduleDatetime)
                          }
                          helperText={
                            touched.scheduleDatetime
                              ? errors.scheduleDatetime
                              : ""
                          }
                          inputVariant="outlined"
                          onChange={(value) => {
                            setFieldValue(
                              "scheduleDatetime",
                              roundMinutes(value)
                            );
                          }}
                          onBlur={() => {
                            setFieldTouched("scheduleDatetime", true);
                          }}
                          KeyboardButtonProps={{
                            "aria-label": "Data e hora de agendamento",
                          }}
                          fullWidth
                        />
                      </Grid>
                    )}
                    {/* end shipping datetime */}

                    {/* expiry time */}
                    <Grid item xs={12} sm={isSchedule() ? 12 : 6}>
                      <Typography gutterBottom>Tempo de Expiração</Typography>
                      <Slider
                        value={values.expiryTime}
                        defaultValue={DEFAULT_EXPIRY_TIME}
                        valueLabelDisplay="on"
                        min={10}
                        max={1439}
                        className={classes.sliderInput}
                        onChange={(e, value) => {
                          setFieldValue("expiryTime", value);
                        }}
                      />
                      <FormHelperText>
                        * Tempo de expiração em minutos.
                      </FormHelperText>
                    </Grid>
                    {/* end expiry time */}

                    {!isFileTypeEmpty() && !isFileWithMessages() && (
                      <Grid item xs={12}>
                        <Divider className={classes.divider} />
                      </Grid>
                    )}

                    {!isFileTypeEmpty() && !isFileWithMessages() && (
                      <Grid item xs={12}>
                        <MessagesSelector
                          messages={values.messages}
                          required
                          onChange={(messages) => {
                            setFieldValue("messages", messages);
                          }}
                        />
                        <FormHelperText>
                          * Obrigatório ao menos uma mensagem.
                        </FormHelperText>
                      </Grid>
                    )}
                  </Grid>
                </div>

                <PageControl>
                  <Button
                    color="default"
                    onClick={() => {
                      clearForm();
                    }}
                  >
                    Limpar
                  </Button>
                  <Button
                    color="primary"
                    disabled={!isValid}
                    onClick={() => {
                      sendMessage(values, () => {
                        clearForm();
                      });
                    }}
                  >
                    Enviar
                  </Button>
                </PageControl>
              </PageBody>
            </React.Fragment>
          );
        }}
      </Formik>
    </AdminPageTemplate>
  );
}

Message.propTypes = {
  breadcrumbs: PropTypes.array,
};

Message.defaultProps = {
  breadcrumbs: [],
};

export default Message;
